import { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider } from './auth/context.tsx';
import RequireAuth from './auth/RequireAuth.tsx';
import TermlyLoader from './consent/TermlyLoader.tsx';
import BetaBanner from './components/BetaBanner.tsx';
import Footer from './components/Footer.tsx';
import SeoHead from './components/SeoHead.tsx';
import SiteHeader from './components/SiteHeader.tsx';
import HomeRoute from './pages/HomeRoute.tsx';
import GuestConsole from './pages/guest/GuestConsole.tsx';
import Integrations from './pages/Integrations.tsx';
import Models from './pages/Models.tsx';
import Pricing from './pages/Pricing.tsx';
import Try from './pages/Try.tsx';
import AgentConsole from './pages/agent/AgentConsole.tsx';
import AgentIntro from './pages/agent/AgentIntro.tsx';
import WorkflowDetails from './pages/agent/WorkflowDetails.tsx';
import MaximusMcpPage from './pages/agent/MaximusMcpPage.tsx';
import Account from './pages/app/Account.tsx';
import AddWidget from './pages/app/AddWidget.tsx';
import AppShell from './pages/app/AppShell.tsx';
import OpenDocument from './pages/app/OpenDocument.tsx';
import StopSchedule from './pages/jobs/StopSchedule.tsx';
import Approve from './pages/auth/Approve.tsx';
import CheckEmail from './pages/auth/CheckEmail.tsx';
import ForgotPassword from './pages/auth/ForgotPassword.tsx';
import ForgotUsername from './pages/auth/ForgotUsername.tsx';
import SignIn from './pages/auth/SignIn.tsx';
import SignUp from './pages/auth/SignUp.tsx';
import SignedOut from './pages/auth/SignedOut.tsx';
import Blog from './pages/Blog.tsx';
import About from './pages/legal/About.tsx';
import AiConsent from './pages/legal/AiConsent.tsx';
import Contact from './pages/legal/Contact.tsx';
import CookiePolicy from './pages/legal/CookiePolicy.tsx';
import CookiePreferences from './pages/legal/CookiePreferences.tsx';
import Licensing from './pages/legal/Licensing.tsx';
import Privacy from './pages/legal/Privacy.tsx';
import Terms from './pages/legal/Terms.tsx';

// DEV-only isolated harness for the Memory Graph panel (local Playwright tests).
// Lazily imported and gated on import.meta.env.DEV, so it never enters prod
// builds.
const MemoryGraphHarness = import.meta.env.DEV
  ? lazy(() => import('./pages/dev/MemoryGraphHarness.tsx'))
  : null;

// DEV-only full agent-console layout harness (real panels + mock data, no auth
// or cloud). Gated on import.meta.env.DEV so it never enters prod builds.
const AgentConsoleHarness = import.meta.env.DEV
  ? lazy(() => import('./pages/dev/AgentConsoleHarness.tsx'))
  : null;

// DEV-only harness for the memory-graph ↔ session-restore data flow (real
// useMemoryGraph + useMemoryGraphSync, test-driven sections). Gated on
// import.meta.env.DEV so it never enters prod builds.
// DEV-only harness for the Connected Tools (MCP) panel. The real panel is
// behind <RequireAuth> + a Maximus-eligible group gate, so an e2e run would
// otherwise need a real signed-in account in the right group on every stage.
// Gated on import.meta.env.DEV so it never enters prod builds.
const McpServersHarness = import.meta.env.DEV
  ? lazy(() => import('./pages/dev/McpServersHarness.tsx'))
  : null;

const MemoryRestoreHarness = import.meta.env.DEV
  ? lazy(() => import('./pages/dev/MemoryRestoreHarness.tsx'))
  : null;

// DEV-only harness for the Workflow Details content panels (real components +
// hostile seed data, no auth or cloud). Gated on import.meta.env.DEV so it
// never enters prod builds.
// DEV-only harness for the document deep-link landing's ready state (the real
// page needs auth + a successful mint before it renders the link). Gated on
// import.meta.env.DEV so it never enters prod builds.
const OpenDocumentHarness = import.meta.env.DEV
  ? lazy(() => import('./pages/dev/OpenDocumentHarness.tsx'))
  : null;

const WorkflowDetailsHarness = import.meta.env.DEV
  ? lazy(() => import('./pages/dev/WorkflowDetailsHarness.tsx'))
  : null;

// DEV-only harness for the signed-in landing (`/app`), which is behind
// <RequireAuth> and so unreachable to an offline layout gate. Gated on
// import.meta.env.DEV so it never enters prod builds.
const AppShellHarness = import.meta.env.DEV
  ? lazy(() => import('./pages/dev/AppShellHarness.tsx'))
  : null;

/** SPA route changes keep the window scroll position — auth pages reached
 *  from below-the-fold engage buttons landed mid-scroll (user report).
 *  Console routes scroll per-panel, so a window reset is always safe. */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppChrome() {
  // The agent console (`/agent/:slug`) is a full-screen app shell with its own
  // bottom tab bar; the site footer would otherwise surface on mobile scroll
  // under that bar. There we drop the footer and let the console fill the
  // viewport (see `.om-shell--console` in AgentConsole.css). Every other route
  // keeps the normal page chrome + footer.
  //
  // NOTE: this universal wrapper is `.om-shell`, NOT `.om-app`. `.om-app` is
  // owned by the `/app` account page (AppShell.css), which constrains itself to
  // a centered 880px column. Sharing that class here once clamped every route
  // to 880px (black bars on desktop, clipped logo); keep them distinct.
  const { pathname } = useLocation();
  // `/dev/console` is the DEV full-console harness; give it the same chrome as a
  // real console route (full-screen shell, no footer) so it faithfully exercises
  // the `.om-shell--console` height chain.
  const isConsole =
    pathname.startsWith('/agent/') || pathname.startsWith('/dev/console') || pathname.startsWith('/guest');
  // The marketing header carries the public Sign in / Sign up (or Console /
  // Sign out) controls. Suppress it on routes that own their own chrome: the
  // console, the /auth/* pages (AuthShell has its own home link), /app
  // (AppShell has its own header), and /try (a focused ad landing that owns its
  // brand mark and deliberately drops nav to cut exits). It shows on /, /legal/*.
  const isOwnChrome =
    isConsole ||
    pathname.startsWith('/auth/') ||
    pathname.startsWith('/app') ||
    pathname === '/try';
  return (
    <div className={isConsole ? 'om-shell om-shell--console' : 'om-shell'}>
      <TermlyLoader />
      <BetaBanner />
      <ScrollToTop />
      <SeoHead />
      {isOwnChrome ? null : <SiteHeader />}
      <main>
        <Routes>
          {/* Public site — signed-in users get redirected to their agent */}
          <Route path="/" element={<HomeRoute />} />
          <Route path="/guest" element={<GuestConsole />} />
          {/* The dedicated agent-info page was removed — agent capabilities now
                live on the home hero (hover) and engaging goes straight to sign-up.
                Redirect any old/bookmarked links home rather than 404. */}
          <Route path="/agent-info/:slug" element={<Navigate to="/" replace />} />
          {/* Public; renders sign-in CTAs for signed-out users. Not yet
                in nav — reachable by direct URL until the page is polished. */}
          <Route path="/integrations" element={<Integrations />} />
          <Route path="/pricing" element={<Pricing />} />
          {/* The blog INDEX is an SPA route; the posts it lists are static files at
              /blog/<file>.html, served from the bucket (see src/config/blogPosts.ts). */}
          <Route path="/blog" element={<Blog />} />
          {/* Public: the open-weight models we publish, with their benchmark
              tables. Linked from /legal/about and the /app landing. */}
          <Route path="/models" element={<Models />} />
          {/* Focused paid-ad landing (Facebook etc.) — minimal chrome + the
              guest dock. Point ad traffic here, not at /guest. */}
          <Route path="/try" element={<Try />} />

          {/* Legal — public, deep-linkable */}
          <Route path="/legal/terms" element={<Terms />} />
          <Route path="/legal/privacy" element={<Privacy />} />
          <Route path="/legal/ai-consent" element={<AiConsent />} />
          <Route path="/legal/about" element={<About />} />
          <Route path="/legal/contact" element={<Contact />} />
          <Route path="/legal/licensing" element={<Licensing />} />
          <Route path="/legal/cookie-policy" element={<CookiePolicy />} />
          <Route path="/legal/cookie-preferences" element={<CookiePreferences />} />

          {/* Auth flows — unlinked from the home page */}
          <Route path="/auth/signin" element={<SignIn />} />
          <Route path="/auth/signup" element={<SignUp />} />
          <Route path="/auth/check-email" element={<CheckEmail />} />
          <Route path="/auth/approve" element={<Approve />} />
          <Route path="/auth/forgot-password" element={<ForgotPassword />} />
          <Route path="/auth/forgot-username" element={<ForgotUsername />} />
          <Route path="/auth/signed-out" element={<SignedOut />} />
          <Route path="/auth/*" element={<Navigate to="/auth/signin" replace />} />

          {/* Protected agent surfaces */}
          <Route
            path="/agent-intro/:slug"
            element={
              <RequireAuth>
                <AgentIntro />
              </RequireAuth>
            }
          />
          <Route
            path="/agent/:slug"
            element={
              <RequireAuth>
                <AgentConsole />
              </RequireAuth>
            }
          />
          {/* Full-page Workflow Details (U12, approved mockup): steps,
              version history + rollback, run history. Console chrome
              applies automatically (isConsole startsWith '/agent/'). */}
          <Route
            path="/agent/:slug/workflows/:workflowId"
            element={
              <RequireAuth>
                <WorkflowDetails />
              </RequireAuth>
            }
          />
          {/* Dedicated "Connected Tools (MCP)" page — full-page console route
              (chrome applies via isConsole startsWith '/agent/'). Gated to the
              MCP agent + beta + admin inside the page. */}
          <Route
            path="/agent/:slug/mcp"
            element={
              <RequireAuth>
                <MaximusMcpPage />
              </RequireAuth>
            }
          />
          <Route
            path="/app"
            element={
              <RequireAuth>
                <AppShell />
              </RequireAuth>
            }
          />
          <Route
            path="/app/account"
            element={
              <RequireAuth>
                <Account />
              </RequireAuth>
            }
          />
          {/* Daily-brief add deep link (2026-08-12 spec §4): carried by the
              CTA on every published widget page. Under /app deliberately —
              postAuthRedirect's allowlist is ^/(agent|app), so a signed-out
              visitor bounced through sign-up returns HERE instead of being
              dropped at /app having lost the brief they asked for. */}
          <Route
            path="/app/widgets/add/:widgetId"
            element={
              <RequireAuth>
                <AddWidget />
              </RequireAuth>
            }
          />
          {/* Document deep link, carried by every agent SMS that texts you one
              of your documents. Under /app for the same postAuthRedirect reason
              as the widget link above — a text is usually opened on a phone
              that isn't signed in, and ^/(agent|app) is what survives the
              sign-in bounce. The presigned URL is minted on THIS page, never
              put in the text: it is 1.5 KB, expires within the hour, and is a
              bearer credential SMS would carry in cleartext. */}
          <Route
            path="/app/documents"
            element={
              <RequireAuth>
                <OpenDocument />
              </RequireAuth>
            }
          />
          <Route path="/app/*" element={<Navigate to="/auth/signin" replace />} />
          {/* Stop-schedule deep link (FR-SCHED-3): carried in every
              scheduled-run email/SMS. Standalone page with site chrome;
              auth-gated — the schedules API is sub-scoped, so a forwarded
              link can't stop anyone else's schedule. */}
          <Route
            path="/jobs/:jobId/stop"
            element={
              <RequireAuth>
                <StopSchedule />
              </RequireAuth>
            }
          />

          {/* DEV-only isolated panel harness (excluded from prod builds) */}
          {MemoryGraphHarness ? (
            <Route
              path="/dev/memory-graph"
              element={
                <Suspense fallback={null}>
                  <MemoryGraphHarness />
                </Suspense>
              }
            />
          ) : null}
          {AgentConsoleHarness ? (
            <Route
              path="/dev/console"
              element={
                <Suspense fallback={null}>
                  <AgentConsoleHarness />
                </Suspense>
              }
            />
          ) : null}
          {McpServersHarness ? (
            <Route
              path="/dev/mcp-servers"
              element={
                <Suspense fallback={null}>
                  <McpServersHarness />
                </Suspense>
              }
            />
          ) : null}
          {MemoryRestoreHarness ? (
            <Route
              path="/dev/memory-restore"
              element={
                <Suspense fallback={null}>
                  <MemoryRestoreHarness />
                </Suspense>
              }
            />
          ) : null}
          {WorkflowDetailsHarness ? (
            <Route
              path="/dev/workflow"
              element={
                <Suspense fallback={null}>
                  <WorkflowDetailsHarness />
                </Suspense>
              }
            />
          ) : null}
          {OpenDocumentHarness ? (
            <Route
              path="/dev/documents"
              element={
                <Suspense fallback={null}>
                  <OpenDocumentHarness />
                </Suspense>
              }
            />
          ) : null}
          {AppShellHarness ? (
            <Route
              path="/dev/app"
              element={
                <Suspense fallback={null}>
                  <AppShellHarness />
                </Suspense>
              }
            />
          ) : null}

          {/* Unknown public path */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {isConsole ? null : <Footer />}
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      {/* NOTE: do NOT opt into the v7 future flags here just to silence the
          console warnings. v7_startTransition defers programmatic navigate()
          calls, which loses the race against the auth-state redirect in our
          delete-account and device-remembered sign-in flows (they landed on
          /auth/signin instead of /auth/signed-out and /app). The v7 upgrade
          is its own tested change; until then the advisory warnings are
          allowlisted in tests/e2e/fixtures/test.ts. */}
      <BrowserRouter>
        <AppChrome />
      </BrowserRouter>
    </AuthProvider>
  );
}
